<template>
  <div class="local-pallet-list" v-loading="loadingData">
    <div class="header-buttons">
      <el-button
        type="primary"
        plain
        style="width: 100px;"
        size="mini"
        @click="toggleTableExpand = !toggleTableExpand"
        >{{ toggleTableExpand ? 'Show' : 'Hide' }} Detail</el-button
      >
    </div>
    <basic-table
      :table-data="pageData"
      :table-headers="LocalPalletsHeaders"
      :has-actions="true"
      :has-row-expand="true"
      :action-min-width="250"
      :checkExpand="item => !toggleTableExpand && item.notes.length > 0"
      :paginationChange="paginationChange"
      :showPagination="false"
    >
      <template v-slot:row-expand="props">
        <div class="note-list f-r">
          <div
            v-for="(note, index) in props.row.notes"
            :key="'note-' + index"
            class="note f-c"
            :class="{
              important: note.status === NoteImportant,
              unimportant: note.status === NoteDeleted
            }"
          >
            <div class="note-content">
              <div class="time">
                {{ getNoteCreatedTime(note) }}
              </div>
              <div
                class="content"
                :class="{
                  striked: note.status === NoteDeleted
                }"
              >
                {{ note.content }}
              </div>
            </div>
            <div class="status-buts f-r">
              <svg-icon
                icon-class="important"
                @click.native="
                  () =>
                    changeStatus(
                      props.$index,
                      props.row,
                      index,
                      note,
                      NoteNormal
                    )
                "
                v-if="note.status === NoteImportant"
              />
              <svg-icon
                icon-class="unimportant"
                @click.native="
                  () =>
                    changeStatus(
                      props.$index,
                      props.row,
                      index,
                      note,
                      NoteImportant
                    )
                "
                v-if="note.status === NoteNormal"
              />
              <svg-icon
                icon-class="trashed"
                @click.native="
                  () =>
                    changeStatus(
                      props.$index,
                      props.row,
                      index,
                      note,
                      note.status === NoteDeleted ? NoteNormal : NoteDeleted
                    )
                "
                :class="{ deleted: note.status === NoteDeleted }"
              />
            </div>
          </div>
        </div>
      </template>
      <template v-slot:row-actions="props">
        <div class="f-r actions">
          <el-button
            size="small"
            class="danger-text"
            type="text"
            @click="openLocalPalletsDialog(props.index, props.row)"
          >
            Edit Group
          </el-button>
          <el-button
            size="small"
            type="text"
            @click="() => printPalletLabels(props.row)"
          >
            Labels
          </el-button>
          <el-button
            size="small"
            type="text"
            class="warning-text"
            @click="() => openExtraLabelsDialog(props.row)"
          >
            Extra Labels
          </el-button>
          <el-button
            size="small"
            class="danger-text"
            type="text"
            @click="deleteLocalPallets(props.index, props.row)"
          >
            Delete
          </el-button>
        </div>
      </template>
    </basic-table>
    <dialog-edit-or-create
      ref="localPalletsEdit"
      :formInputInit="InitLocalPalletsForm"
    />
    <common-dialog
      ref="extraLabelsDialog"
      title="Extra Labels"
      :inputs-map="morePalletForm"
      :save-cb="morePalletLabels"
      :reset-inputs="resetMoreCount"
    />
  </div>
</template>
<script>
import cloneDeep from 'lodash/cloneDeep'
import {
  LocalPalletsHeaders,
  MoreLocalPalletsForm,
  InitLocalPalletsForm,
  LocalPalletGroupWrapUp
} from '@/core/models/order'
import { NoteDeleted, NoteNormal, NoteImportant } from '@/core/models/note'
// msg
import PagesMessage from '@/core/message/pages'
// components
import BasicTable from '@/components/common/BasicTable.vue'
import DialogEditOrCreate from '@/components/common/DialogEditOrCreate.vue'
import CommonDialog from '@/components/common/CommonDialog.vue'
import {
  labelLocalPalletsGroup,
  modifyLocalPallets,
  removeLocalPallets
} from '@/api/order'
// api
import { viewPdf } from '@/core/utils'
import { queryGoods } from '@/api/goods'
import { queryAddress } from '@/api/address'
export default {
  name: 'LocalPalletsInfo',
  components: { BasicTable, DialogEditOrCreate, CommonDialog },
  props: {
    localPallets: {
      type: Array,
      required: true
    },
    orderId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      LocalPalletsHeaders,
      NoteDeleted,
      NoteNormal,
      NoteImportant,
      morePalletForm: cloneDeep(MoreLocalPalletsForm),
      palletGroups: [],
      loadingData: false,
      toggleTableExpand: false
    }
  },
  computed: {
    pageData() {
      return {
        records: this.palletGroups,
        total: this.localPallets.length,
        size: this.localPallets.length,
        current: 1
      }
    },
    goodsIds() {
      return this.localPallets.map(one => one.goodsId)
    }
  },
  watch: {
    goodsIds: {
      handler() {
        this.getPalletGoods()
      },
      immediate: true
    }
  },
  methods: {
    InitLocalPalletsForm,
    sortChange(sort) {},
    paginationChange(key, path, value) {},
    getNoteCreatedTime(note) {
      const date = new Date(note.createdTime)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    },
    async getPalletGoods() {
      this.loadingData = true
      const res = await queryGoods({
        ids: this.goodsIds,
        size: this.goodsIds.length
      })
      const goodsList = res.records
      const addressIds = goodsList.map(one => one.addressId)
      const addressPage = await queryAddress({ addressIds, size: -1 })
      const addressMap = Object.fromEntries(
        addressPage.records.map(one => [one.id, one])
      )
      this.palletGroups.splice(0, this.palletGroups.length)
      this.localPallets.forEach((group, index) => {
        const goods = goodsList.find(one => one.id === group.goodsId)
        this.palletGroups.push({
          ...group,
          id: index,
          refNo: goods.referenceNo,
          fba: goods.shipmentNo,
          address: addressMap[goods.addressId]
        })
      })
      this.loadingData = false
    },
    checkLocalPallets(data, isUpdate, oldIndex) {
      if (isUpdate) {
        const old = this.localPallets[oldIndex]
        return JSON.stringify(old) !== JSON.stringify(data)
      } else {
        const index = this.localPallets.findIndex(one => {
          return (
            one.addressCode === data.addressCode || one.refNo === data.refNo
          )
        })
        return index === -1
      }
    },
    doModify(index, data, done, error) {
      const flag = this.checkLocalPallets(data, index > -1, index)
      if (!flag) return [flag, 'Duplicated pallets info!']
      const original = this.palletGroups[index]
      data = LocalPalletGroupWrapUp(data, original)
      modifyLocalPallets(this.orderId, original.goodsId, data)
        .then(res => {
          PagesMessage.emit(PagesMessage.eventMap.REFRESH_ORDER_DETAIL)
          this.$message.success('Successfully!')
          done()
          const extraPallets = res.extraPallets
          if (extraPallets && extraPallets.length > 0) {
            labelLocalPalletsGroup(
              this.orderId,
              original.goodsId,
              original.palletCount,
              -1
            ).then(res => {
              viewPdf(res)
            })
          }
        })
        .catch(err => error(err))
    },
    createLocalPallets() {
      return (data, done, error) => this.doModify(-1, data, done, error)
    },
    updateLocalPallets(index) {
      return (data, done, error) => {
        this.doModify(index, data, done, error)
      }
    },
    deleteLocalPallets(index, data) {
      const original = this.palletGroups[index]
      this.$confirm(
        `Delete pallets ${data.address.code + ' ' + data.refNo}, continue?`,
        'Confirm',
        {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      )
        .then(() => {
          removeLocalPallets(this.orderId, original.goodsId).then(res => {
            if (res) {
              PagesMessage.emit(PagesMessage.eventMap.REFRESH_ORDER_DETAIL)
              this.$message.success('Successfully!')
            }
          })
        })
        .catch(() => {})
    },
    openLocalPalletsDialog(index, data) {
      this.$refs.localPalletsEdit.open(
        data,
        data ? this.updateLocalPallets(index) : this.createLocalPallets()
      )
    },
    printPalletLabels(group) {
      labelLocalPalletsGroup(this.orderId, group.goodsId, 0, -1).then(res => {
        viewPdf(res)
      })
    },
    openExtraLabelsDialog(group) {
      this.morePalletForm[0].value = group.address
      this.morePalletForm[1].value = group.palletCount
      this.$refs.extraLabelsDialog.open()
    },
    morePalletLabels(inputs) {
      const address = inputs.address
      const original = this.palletGroups.find(
        one => one.address.id === address.id
      )
      const count = inputs.moreCount + this.morePalletForm[1].value
      modifyLocalPallets(this.orderId, original.goodsId, {
        palletCount: count,
        appendToShipment: inputs.appendToShipment
      }).then(res => {
        if (res) {
          PagesMessage.emit(PagesMessage.eventMap.REFRESH_ORDER_DETAIL)
          this.$message.success('Successfully!')
          const extraPallets = res.extraPallets
          if (extraPallets && extraPallets.length > 0) {
            labelLocalPalletsGroup(
              this.orderId,
              original.goodsId,
              original.palletCount,
              -1
            ).then(res => {
              viewPdf(res)
            })
            this.$refs.extraLabelsDialog.close()
          }
        } else {
          this.$message.error('More pallets failed!')
        }
      })
      return [false, '']
    },
    resetMoreCount() {
      this.morePalletForm[2].value = this.morePalletForm[2].default
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/styles/common.scss';
@import '@/assets/styles/note-list.scss';

::v-deep .el-table__fixed-right::before {
  display: none !important;
}

.header-buttons {
  margin-bottom: 10px;
}
</style>
